<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <!-- Loading State -->
    <empty-state
      v-if="loading.fetchDailyMoreRestaurants"
      :title="$t('loading')"
      :loading="true"
    />

    <template v-else>
      <v-row v-if="hasOwnFilters" class="mb-2">
        <v-col cols="12" lg="auto">
          <report-meals-select-companies></report-meals-select-companies>
        </v-col>
      </v-row>

      <!-- Empty State -->
      <empty-state
        v-if="companyReservedMeals.length === 0"
        icon="mdi-food-off"
        :title="$t('no_reserved_meals_data')"
        :subtitle="$t('select_date_and_company')"
      />

      <div v-else>
        <template v-for="company in companyReservedMeals">
          <h2 class="text-center mt-5 mb-2">
            {{ toLatin(company.company) }}
          </h2>
          <v-data-table
            id="table9"
            :headers="getMobileHeaders(headers)"
            :items="customSearch(company.foods, headers, filters.search)"
            class="striped-table ordera-box-shadow"
            hide-default-footer
            disable-pagination
            mobile-breakpoint="0"
            :loading="loading.fetchDailyMoreRestaurants"
          >
            <template v-slot:no-data>
              <empty-state
                icon="mdi-food-off"
                :title="$t('no_meals_in_table')"
                :subtitle="$t('no_data_for_selected_filters')"
              />
            </template>
            <template v-slot:item.menu="{ item }">
              <span @click="showMeal(item.id)">{{ toLatin(item.name) }}</span>
            </template>
            <template v-slot:item.num="{ item, index }">
              {{ item.name !== $t("total") ? index + 1 : "" }}
            </template>
            <template v-slot:item.actions="{ item }">
              <circle-button
                icon="mdi-account-group"
                color="primary"
                :tooltip="$t('show_employees_who_ordered')"
                @click="openEmployeesDialog(item, company)"
              />
            </template>
            <template v-slot:body.append>
              <tr>
                <td v-if="!device.isMobile"></td>
                <td style="font-weight: bold">{{ $t("totalU") }}</td>
                <td style="font-weight: bold">
                  {{ totalShift(company.foods, "shift_one") }}
                </td>
                <td style="font-weight: bold">
                  {{ totalShift(company.foods, "shift_two") }}
                </td>
                <td style="font-weight: bold">
                  {{ totalShift(company.foods, "shift_three") }}
                </td>
                <td style="font-weight: bold">
                  {{ totalShift(company.foods, "shift_four") }}
                </td>
                <td style="font-weight: bold">
                  {{ totalShift(company.foods, "total") }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </template>
      </div>
    </template>

    <employees-who-ordered-dialog
      v-model="employeesDialog"
      :selected-meal="selectedMeal"
      :selected-company="selectedCompany"
      :filters="filters"
    />
  </div>
</template>

<script>
import reportService from "@/services/report";
import ReportMealsSelectCompanies from "@/components/report/meals/fields/ReportMealsSelectCompaniesField.vue";
import EmployeesWhoOrderedDialog from "@/components/report/meals/dialogs/EmployeesWhoOrderedDialog.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";

export default {
  mixins: [hasMobileHeaders],
  components: {
    ReportMealsSelectCompanies,
    EmployeesWhoOrderedDialog,
    EmptyState,
  },
  data: () => ({
    report: null,
    loading: {
      fetchDailyMoreRestaurants: false,
    },
    employeesDialog: false,
    selectedMeal: null,
    selectedCompany: null,
  }),
  methods: {
    async fetchDailyMoreRestaurants() {
      if (this.loading.fetchDailyMoreRestaurants) {
        console.warn(
          "Fetch daily more restaurants operation already in progress, skipping duplicate request"
        );
        return;
      }

      this.loading.fetchDailyMoreRestaurants = true;

      const params = {
        date: this.filters.dates.from,
        date2: this.filters.dates.to,
      };

      try {
        const response = await reportService.getReservedMeals(
          this.$store.state.restaurant.id,
          params
        );
        this.report = response.data;
      } catch (error) {
        console.error("Error fetching reserved meals:", error);
      } finally {
        this.loading.fetchDailyMoreRestaurants = false;
      }
    },
    totalShift(foods, shiftKey) {
      return foods.reduce((acc, food) => acc + (food[shiftKey] || 0), 0);
    },
    openEmployeesDialog(meal, company) {
      this.selectedMeal = meal;
      this.selectedCompany = company;
      this.employeesDialog = true;
    },
  },
  computed: {
    /** Stari izbori firme/smena iznad tabele; nova ljuska (računar) ih ima u zajedničkoj traci, pa ih isključuje. */
    hasOwnFilters() {
      return true;
    },
    selectedMealCompanies() {
      return this.$store.state.report.selectedMealCompanies || [];
    },
    companyReservedMeals() {
      if (!this.report) return [];
      const result = [];

      // Get selected company names from store
      const selectedCompanyNames = this.selectedMealCompanies.map((c) => c.name);

      Object.entries(this.report).forEach(([companyName, shifts]) => {
        // Filter by selected companies from store
        if (!selectedCompanyNames.includes(companyName)) {
          return;
        }

        const foods = {};

        // Process each shift
        Object.entries(shifts).forEach(([shiftKey, meals]) => {
          const shiftField =
            shiftKey === "1-smena"
              ? "shift_one"
              : shiftKey === "2-smena"
              ? "shift_two"
              : shiftKey === "3-smena"
              ? "shift_three"
              : shiftKey === "4-smena"
              ? "shift_four"
              : null; // ili neka default vrednost ako je potrebno

          Object.entries(meals).forEach(([mealName, mealData]) => {
            if (!foods[mealName]) {
              foods[mealName] = {
                id: mealData.id,
                name: mealName,
                shift_one: 0,
                shift_two: 0,
                shift_three: 0,
                shift_four: 0,
                total: 0,
              };
            }
            foods[mealName][shiftField] += mealData.quantity;
            foods[mealName].total += mealData.quantity;
          });
        });

        const selectedCompany = this.selectedMealCompanies.find(
          (c) => c.name === companyName
        );
        // Add the company and its foods to the result
        result.push({
          company: companyName,
          id: selectedCompany?.id || null,
          foods: Object.values(foods),
        });
      });

      return result;
    },
    companies() {
      if (!this.report) return [];
      return Object.keys(this.report);
    },
    filters() {
      return this.$store.state.report.filters;
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num" },
        { text: this.$t("meal"), value: "name" },
        { text: this.$t("1st_shift"), value: "shift_one" },
        { text: this.$t("2nd_shift"), value: "shift_two" },
        { text: this.$t("3rd_shift"), value: "shift_three" },
        { text: this.$t("4th_shift"), value: "shift_four" },
        { text: this.$t("total"), value: "total" },
        { text: this.$t("actions"), value: "actions", sortable: false },
      ];
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.fetchDailyMoreRestaurants();
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
